import { 
  Sparkles, 
  Music, 
  Heart, 
  Server, 
  BookOpen, 
  Users, 
  Building2, 
  ArrowRight,
  Zap,
  Globe,
  Shield,
  Layers,
  Play,
  PenTool,
  TrendingUp,
  Target
} from 'lucide-react';
import { Button } from '../components/ui/button';

const TechnologyPage = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="technology-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#E8E3D8]" data-testid="navigation">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <a href="https://temporarysatisfaction.com" className="font-heading text-xl sm:text-2xl font-semibold text-[#2A2A2A] hover:text-[#5C5C5A] transition-colors" data-testid="nav-logo">
              Temporary Satisfaction
            </a>
            <div className="hidden md:flex items-center gap-8">
              <a href="https://temporarysatisfaction.com" className="text-sm font-medium text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors" data-testid="nav-main-site">Main Site</a>
              <a href="#platform" className="text-sm font-medium text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors" data-testid="nav-platform">Platform</a>
              <a href="#products" className="text-sm font-medium text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors" data-testid="nav-products">Products</a>
              <a href="#impact" className="text-sm font-medium text-[#5C5C5A] hover:text-[#2A2A2A] transition-colors" data-testid="nav-impact">Impact</a>
              <a href="/partners" className="btn-primary text-sm py-2 px-5" data-testid="nav-cta">
                Partner With Us
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 sm:pt-40 pb-24 sm:pb-32 overflow-hidden" data-testid="hero-section">
        {/* Background blobs */}
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#E8D5D1]/30 blob" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#CDE3EB]/30 blob" style={{ animationDelay: '-3s' }} />
        <div className="absolute top-1/2 left-1/4 w-[300px] h-[300px] bg-[#C2D1C2]/20 blob" style={{ animationDelay: '-5s' }} />
        
        <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-in-up">
              <span className="overline block mb-6" data-testid="hero-overline">The AI-Powered Ecosystem</span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] text-[#2A2A2A] mb-6" data-testid="hero-title">
                Technology that Cares, Learns, and Builds Life.
              </h1>
              <p className="text-lg sm:text-xl text-[#5C5C5A] leading-relaxed mb-10 max-w-xl" data-testid="hero-description">
                A scalable software ecosystem empowering caregiving, creativity, and long-term life building through intentional digital tools.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#platform" className="btn-primary flex items-center gap-2" data-testid="hero-cta-primary">
                  Explore the Platform
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a href="/partners" className="btn-secondary" data-testid="hero-cta-secondary">
                  Partner With Us
                </a>
              </div>
            </div>
            
            <div className="animate-fade-in-up animation-delay-400 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1738082956233-b8f1803411d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGNvbm5lY3RlZCUyMG5ldHdvcmslMjBzb2Z0JTIwcGFzdGVsJTIwd2FybSUyMHRlY2hub2xvZ3klMjBlY29zeXN0ZW18ZW58MHx8fHwxNzc0OTMxNTgzfDA&ixlib=rb-4.1.0&q=85" 
                  alt="Connected ecosystem visualization representing the AI-powered platform"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7]/40 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What the Platform Is */}
      <section id="platform" className="py-24 sm:py-32 relative overflow-hidden" data-testid="platform-overview-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#CDE3EB]/10 rounded-full blur-3xl" />
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#C2D1C2]/10 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center relative">
          <span className="overline block mb-6" data-testid="platform-overline">Platform Overview</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#2A2A2A] mb-8" data-testid="platform-title">
            More Than Software.<br />A Foundation for Human Growth.
          </h2>
          <p className="text-lg sm:text-xl text-[#5C5C5A] leading-relaxed max-w-3xl mx-auto" data-testid="platform-description">
            We build secure, AI-powered infrastructure that bridges the gap between digital efficiency and human empathy. Our ecosystem scales to meet the needs of families, creators, caregivers, and enterprise partners seeking meaningful technology solutions.
          </p>
        </div>
      </section>

      {/* Core Technology Capabilities */}
      <section className="py-24 sm:py-32 bg-white" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-16">
            <span className="overline block mb-6" data-testid="capabilities-overline">Core Capabilities</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#2A2A2A]" data-testid="capabilities-title">
              Built on Four Pillars of Innovation
            </h2>
          </div>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-testid="capabilities-grid">
            {/* AI Content Generation - Large Card */}
            <div className="lg:col-span-2 card-premium p-8 sm:p-10" data-testid="capability-ai-content">
              <div className="w-14 h-14 rounded-2xl bg-[#E8D5D1]/40 flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-[#2A2A2A]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-medium text-[#2A2A2A] mb-4">
                AI-Powered Content Generation
              </h3>
              <p className="text-[#5C5C5A] leading-relaxed mb-6">
                Ethical, scalable storytelling and creative content generation. Personalized stories, songs, and guided educational experiences designed with care and cultural sensitivity.
              </p>
              <ul className="space-y-3 text-[#5C5C5A]">
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C2D1C2]" />
                  Personalized story generation
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C2D1C2]" />
                  Custom song creation
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C2D1C2]" />
                  Guided emotional learning experiences
                </li>
              </ul>
            </div>
            
            {/* Audio/Media Layer - Tall Card */}
            <div className="lg:row-span-2 card-premium p-8 sm:p-10 flex flex-col" data-testid="capability-audio">
              <div className="w-14 h-14 rounded-2xl bg-[#EBDCB2]/40 flex items-center justify-center mb-6">
                <Music className="w-7 h-7 text-[#2A2A2A]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl font-medium text-[#2A2A2A] mb-4">
                Intelligent Media & Audio Layer
              </h3>
              <p className="text-[#5C5C5A] leading-relaxed mb-6 flex-grow">
                Immersive, accessible audio experiences. From warm narrated story playback to original song generation, our media layer creates premium, emotionally resonant content.
              </p>
              <div className="mt-auto space-y-4">
                <div className="flex items-center gap-3 text-sm text-[#5C5C5A]">
                  <Play className="w-4 h-4" strokeWidth={1.5} />
                  Narrated playback
                </div>
                <div className="flex items-center gap-3 text-sm text-[#5C5C5A]">
                  <Globe className="w-4 h-4" strokeWidth={1.5} />
                  Multilingual ready
                </div>
                <div className="flex items-center gap-3 text-sm text-[#5C5C5A]">
                  <Zap className="w-4 h-4" strokeWidth={1.5} />
                  Adaptive delivery
                </div>
              </div>
            </div>
            
            {/* Caregiver Support - Square Card */}
            <div className="card-premium p-8 sm:p-10" data-testid="capability-caregiver">
              <div className="w-14 h-14 rounded-2xl bg-[#CDE3EB]/40 flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-[#2A2A2A]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-medium text-[#2A2A2A] mb-4">
                Caregiver & Learning Support
              </h3>
              <p className="text-[#5C5C5A] leading-relaxed text-sm">
                Context-aware assistance for families. Routine support, move & play tools, and guided activities that adapt to real-world caregiving needs.
              </p>
            </div>
            
            {/* Scalable Architecture - Wide Card */}
            <div className="md:col-span-2 lg:col-span-1 card-premium p-8 sm:p-10" data-testid="capability-architecture">
              <div className="w-14 h-14 rounded-2xl bg-[#C2D1C2]/40 flex items-center justify-center mb-6">
                <Server className="w-7 h-7 text-[#2A2A2A]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-medium text-[#2A2A2A] mb-4">
                Scalable Platform Architecture
              </h3>
              <p className="text-[#5C5C5A] leading-relaxed text-sm mb-4">
                Enterprise-grade infrastructure with user accounts, content management, and subscription-ready systems.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#E8D5D1]/30 text-[#2A2A2A]">
                  User Accounts
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#C2D1C2]/30 text-[#2A2A2A]">
                  Admin Tools
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#EBDCB2]/30 text-[#2A2A2A]">
                  API Ready
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Powered by the Platform */}
      <section id="products" className="py-24 sm:py-32" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-20">
            <span className="overline block mb-6" data-testid="products-overline">Active Products</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#2A2A2A]" data-testid="products-title">
              Products Powered by Our Platform
            </h2>
          </div>
          
          {/* Product 1: MyStoryWorld */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24" data-testid="product-mystoryworld">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8D5D1]/30 text-sm font-medium text-[#2A2A2A] mb-6">
                <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                Learning & Creativity
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-medium text-[#2A2A2A] mb-4">
                MyStoryWorld
              </h3>
              <p className="text-lg text-[#5C5C5A] leading-relaxed mb-6">
                An AI-powered storytelling platform that creates personalized, educational stories for children. Fostering creativity, literacy, and emotional development through the magic of narrative.
              </p>
              <ul className="space-y-3 text-[#5C5C5A] mb-6">
                <li className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-[#C2D1C2] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  Personalized story generation based on child's interests
                </li>
                <li className="flex items-start gap-3">
                  <Music className="w-5 h-5 text-[#C2D1C2] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  Original songs with warm, engaging audio
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-[#C2D1C2] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  Designed for family moments and learning
                </li>
              </ul>
              <a href="/mystoryworld" className="inline-flex items-center gap-2 text-[#2A2A2A] font-medium hover:gap-3 transition-all" data-testid="mystoryworld-link">
                Learn more <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.pexels.com/photos/36697976/pexels-photo-36697976.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Parent and child enjoying a cozy storytelling moment together"
                  className="w-full h-[350px] object-cover"
                  data-testid="product-mystoryworld-image"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#EBDCB2]/40 rounded-full blur-2xl" />
            </div>
          </div>
          
          {/* Product 2: MyStoryWorld Care */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24" data-testid="product-mystoryworld-care">
            <div className="order-2 lg:order-1 relative">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.pexels.com/photos/8613089/pexels-photo-8613089.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Teacher reading to children in a warm nurturing childcare setting"
                  className="w-full h-[350px] object-cover"
                  data-testid="product-care-image"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#CDE3EB]/40 rounded-full blur-2xl" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#CDE3EB]/30 text-sm font-medium text-[#2A2A2A] mb-6">
                <Heart className="w-4 h-4" strokeWidth={1.5} />
                Family & Caregiver Support
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-medium text-[#2A2A2A] mb-4">
                MyStoryWorld Care
              </h3>
              <p className="text-lg text-[#5C5C5A] leading-relaxed mb-6">
                Extending the MyStoryWorld experience to support caregivers, families with special needs, and intergenerational connection. Tools designed with empathy at their core.
              </p>
              <ul className="space-y-3 text-[#5C5C5A] mb-6">
                <li className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-[#E8D5D1] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  Caregiver-facing support layer
                </li>
                <li className="flex items-start gap-3">
                  <Layers className="w-5 h-5 text-[#E8D5D1] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  Move & play tools for routine support
                </li>
                <li className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-[#E8D5D1] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  Activity systems for daily engagement
                </li>
              </ul>
              <a href="/care" className="inline-flex items-center gap-2 text-[#2A2A2A] font-medium hover:gap-3 transition-all" data-testid="care-link">
                Learn more <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
          
          {/* Product 3: TS Ad Foundry */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-testid="product-ts-ad-foundry">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C2D1C2]/30 text-sm font-medium text-[#2A2A2A] mb-6">
                <PenTool className="w-4 h-4" strokeWidth={1.5} />
                Creator & Business Tools
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-medium text-[#2A2A2A] mb-4">
                TS Ad Foundry
              </h3>
              <p className="text-lg text-[#5C5C5A] leading-relaxed mb-6">
                Ethical, automated content generation for creators and small businesses. Empowering entrepreneurs to create professional marketing content without the complexity.
              </p>
              <ul className="space-y-3 text-[#5C5C5A] mb-6">
                <li className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-[#EBDCB2] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  Automated ad content generation
                </li>
                <li className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-[#EBDCB2] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  Small business focused workflows
                </li>
                <li className="flex items-start gap-3">
                  <TrendingUp className="w-5 h-5 text-[#EBDCB2] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  Scalable creator support
                </li>
              </ul>
              <a href="/ts-ad-foundry" className="inline-flex items-center gap-2 text-[#2A2A2A] font-medium hover:gap-3 transition-all" data-testid="foundry-link">
                Learn more <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img 
                  src="https://images.pexels.com/photos/17499879/pexels-photo-17499879.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Modern creative workspace with content creation tools"
                  className="w-full h-[350px] object-cover"
                  data-testid="product-foundry-image"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#C2D1C2]/40 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Who It Serves */}
      <section className="py-24 sm:py-32 bg-white" data-testid="audience-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-16">
            <span className="overline block mb-6" data-testid="audience-overline">Who We Serve</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#2A2A2A]" data-testid="audience-title">
              Built for Those Who Build Life
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8" data-testid="audience-grid">
            {/* Families & Caregivers */}
            <div className="glass-panel rounded-3xl p-8 sm:p-10" data-testid="audience-families">
              <div className="w-16 h-16 rounded-2xl bg-[#E8D5D1]/40 flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-[#2A2A2A]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-medium text-[#2A2A2A] mb-4">
                Families & Caregivers
              </h3>
              <p className="text-[#5C5C5A] leading-relaxed">
                Parents, guardians, and caregivers seeking meaningful digital tools that support learning, bonding, and daily routines without screen-time guilt.
              </p>
            </div>
            
            {/* Creators & Storytellers */}
            <div className="glass-panel rounded-3xl p-8 sm:p-10" data-testid="audience-creators">
              <div className="w-16 h-16 rounded-2xl bg-[#EBDCB2]/40 flex items-center justify-center mb-6">
                <PenTool className="w-8 h-8 text-[#2A2A2A]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-medium text-[#2A2A2A] mb-4">
                Creators & Storytellers
              </h3>
              <p className="text-[#5C5C5A] leading-relaxed">
                Writers, artists, educators, and small business owners who need ethical AI tools to amplify their creative voice and reach their audiences.
              </p>
            </div>
            
            {/* Community & Enterprise */}
            <div className="glass-panel rounded-3xl p-8 sm:p-10" data-testid="audience-enterprise">
              <div className="w-16 h-16 rounded-2xl bg-[#C2D1C2]/40 flex items-center justify-center mb-6">
                <Building2 className="w-8 h-8 text-[#2A2A2A]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl font-medium text-[#2A2A2A] mb-4">
                Community & Enterprise Partners
              </h3>
              <p className="text-[#5C5C5A] leading-relaxed">
                Schools, nonprofits, care facilities, and mission-aligned organizations seeking scalable technology that aligns with their values.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Innovation & Impact */}
      <section id="impact" className="py-24 sm:py-32 relative overflow-hidden" data-testid="impact-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7] to-[#E8D5D1]/10" />
        
        <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
          <div className="text-center mb-20">
            <span className="overline block mb-6" data-testid="impact-overline">Why It Matters</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#2A2A2A] mb-6" data-testid="impact-title">
              Technology with Purpose
            </h2>
            <p className="text-lg text-[#5C5C5A] max-w-2xl mx-auto" data-testid="impact-description">
              In a world of overwhelming digital noise, we build tools that nurture rather than distract. Our platform represents a new paradigm in human-centered AI.
            </p>
          </div>
          
          {/* Impact Metrics */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-20" data-testid="impact-metrics">
            <div className="text-center" data-testid="metric-products">
              <div className="font-heading text-5xl sm:text-6xl font-medium text-[#2A2A2A] mb-2">3</div>
              <div className="text-[#5C5C5A] font-medium">Active Products</div>
            </div>
            <div className="text-center" data-testid="metric-pillars">
              <div className="font-heading text-5xl sm:text-6xl font-medium text-[#2A2A2A] mb-2">4</div>
              <div className="text-[#5C5C5A] font-medium">Technology Pillars</div>
            </div>
            <div className="text-center" data-testid="metric-audiences">
              <div className="font-heading text-5xl sm:text-6xl font-medium text-[#2A2A2A] mb-2">5+</div>
              <div className="text-[#5C5C5A] font-medium">Audience Segments</div>
            </div>
            <div className="text-center" data-testid="metric-scalable">
              <div className="font-heading text-5xl sm:text-6xl font-medium text-[#2A2A2A] mb-2">1</div>
              <div className="text-[#5C5C5A] font-medium">Unified Platform</div>
            </div>
          </div>
          
          {/* Future Readiness */}
          <div className="grid md:grid-cols-2 gap-8" data-testid="future-readiness">
            <div className="card-premium p-8 sm:p-10" data-testid="future-scalability">
              <h3 className="font-heading text-xl font-medium text-[#2A2A2A] mb-4">
                Scalable by Design
              </h3>
              <p className="text-[#5C5C5A] leading-relaxed">
                Our architecture supports growth from individual families to enterprise deployments. Subscription-ready, API-enabled, and built for multi-product expansion.
              </p>
            </div>
            <div className="card-premium p-8 sm:p-10" data-testid="future-innovation">
              <h3 className="font-heading text-xl font-medium text-[#2A2A2A] mb-4">
                Future-Ready Innovation
              </h3>
              <p className="text-[#5C5C5A] leading-relaxed">
                Positioned for emerging opportunities: tutoring systems, life skills pathways, multilingual expansion, and deeper AI personalization across all products.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 sm:py-32" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <div className="relative rounded-[2.5rem] overflow-hidden p-12 sm:p-16 lg:p-20" data-testid="cta-container">
            {/* Gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#EBDCB2]/60 via-[#E8D5D1]/40 to-[#CDE3EB]/30" />
            <div className="absolute inset-0 bg-[#FDFBF7]/40" />
            
            <div className="relative text-center">
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#2A2A2A] mb-6" data-testid="cta-title">
                Partner with the Future of Care
              </h2>
              <p className="text-lg text-[#5C5C5A] mb-10 max-w-xl mx-auto" data-testid="cta-description">
                Whether you're a funder, partner, or pilot organization, we'd love to explore how this ecosystem can support your mission.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="/partners" className="btn-secondary flex items-center gap-2" data-testid="cta-partner">
                  Become a Partner
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a href="/partners" className="btn-primary" data-testid="cta-contact">
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-[#E8E3D8]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <a href="https://temporarysatisfaction.com" className="font-heading text-lg font-semibold text-[#2A2A2A] hover:text-[#5C5C5A] transition-colors">
                Temporary Satisfaction
              </a>
              <div className="flex items-center gap-8 text-sm text-[#5C5C5A]">
                <a href="https://temporarysatisfaction.com" className="hover:text-[#2A2A2A] transition-colors">Return to Temporary Satisfaction</a>
                <a href="/mystoryworld" className="hover:text-[#2A2A2A] transition-colors">MyStoryWorld</a>
                <a href="/care" className="hover:text-[#2A2A2A] transition-colors">MyStoryWorld Care</a>
                <a href="/ts-ad-foundry" className="hover:text-[#2A2A2A] transition-colors">TS Ad Foundry</a>
              </div>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-6 border-t border-[#E8E3D8]/50">
              <p className="text-sm text-[#5C5C5A]">Part of the Temporary Satisfaction ecosystem</p>
              <p className="text-sm text-[#5C5C5A]">&copy; {new Date().getFullYear()} Temporary Satisfaction. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TechnologyPage;
